/**
 * The data-free Marble Context workspace demonstrator (#1650, Slice A).
 *
 * It deliberately knows no entity keys, SDK, routes, provider, rules or write
 * doors. Hosts supply already-governed projections and callbacks. The finite
 * unions below are the visible grammar: adding arbitrary component/HTML/SQL
 * blocks is not an escape hatch hidden in `children`.
 */
import type { ReactNode } from 'react'
import { Archive, Box, Check, ChevronRight, Clock3, GitBranch, Plus, RotateCcw, Save, Search } from 'lucide-react'
import { Button } from '../controls/Button'
import { Pill, type Tone } from '../controls/Pill'
import { SegmentedControl } from '../controls/SegmentedControl'
import s from './ContextWorkspace.module.css'

export type ContextWorkspaceMode = 'canvas' | 'relations' | 'thread' | 'actions'
export type ContextSessionState = 'active' | 'saved' | 'archived'
export type ContextBlockKind =
  | 'record' | 'snapshot' | 'query' | 'result' | 'collection' | 'relation'
  | 'note' | 'decision' | 'ai' | 'action' | 'outcome' | 'measure'
export type ContextSourceState = 'live' | 'captured' | 'stale' | 'omitted' | 'superseded'
export type ContextTrayKind = 'record' | 'snapshot' | 'query' | 'collection' | 'evidence' | 'result'

export interface ContextSessionSummary {
  title: string
  objective: string
  state: ContextSessionState
  syncLabel?: string
  eyebrow?: string
}

export interface ContextTrayEntry {
  id: string
  kind: ContextTrayKind
  title: string
  detail?: string
  count?: number
}

export interface ContextBlock {
  id: string
  kind: ContextBlockKind
  title: string
  summary?: string
  meta?: string
  sourceState?: ContextSourceState
  tone?: Tone
  content?: ReactNode
}

export interface ContextThreadEntry {
  id: string
  actor: string
  kind: 'human' | 'copilot' | 'system' | 'decision' | 'assignment' | 'outcome'
  body: string
  time?: string
  linkedBlockTitle?: string
}

export interface ContextActionEntry {
  id: string
  title: string
  consequence: string
  status: 'proposed' | 'ready' | 'running' | 'approval' | 'stale' | 'done' | 'refused'
  affected?: number
  refused?: number
  refreshable?: boolean
  reviewable?: boolean
  handoffable?: boolean
}

export interface ContextRelationEntry {
  id: string
  fromId: string
  fromTitle: string
  toId: string
  toTitle: string
  kind: 'supports' | 'contradicts' | 'relates' | 'supersedes' | 'depends_on'
  confidence?: number
  rationale?: string
}

export interface ContextWorkspaceProps {
  session: ContextSessionSummary
  tray: readonly ContextTrayEntry[]
  blocks: readonly ContextBlock[]
  thread: readonly ContextThreadEntry[]
  actions: readonly ContextActionEntry[]
  relations?: readonly ContextRelationEntry[]
  mode: ContextWorkspaceMode
  onModeChange: (mode: ContextWorkspaceMode) => void
  onAddContext?: () => void
  onOpenTrayEntry?: (id: string) => void
  onOpenBlock?: (id: string) => void
  onRefreshAction?: (id: string) => void
  onReviewAction?: (id: string) => void
  onHandoffAction?: (id: string) => void
  onLifecycle?: (act: 'save' | 'archive' | 'resume' | 'restore') => void
  composerValue?: string
  onComposerChange?: (value: string) => void
  onSubmitPrompt?: () => void
  composerBusy?: boolean
  testId?: string
}

const BLOCK_LABEL: Record<ContextBlockKind, string> = {
  record: 'Live record', snapshot: 'Snapshot', query: 'Live query', result: 'Result set',
  collection: 'Collection', relation: 'Relations', note: 'Note', decision: 'Decision',
  ai: 'AI finding', action: 'Action proposal', outcome: 'Outcome', measure: 'Measure',
}
const SOURCE_LABEL: Record<ContextSourceState, string> = {
  live: 'Live', captured: 'Captured', stale: 'Refresh needed', omitted: 'Not available', superseded: 'Superseded',
}
const SOURCE_TONE: Record<ContextSourceState, Tone> = {
  live: 'good', captured: 'brand', stale: 'warn', omitted: 'critical', superseded: 'neutral',
}
const ACTION_TONE: Record<ContextActionEntry['status'], Tone> = {
  proposed: 'neutral', ready: 'good', running: 'brand', approval: 'warn', stale: 'warn', done: 'good', refused: 'critical',
}

function SessionLifecycleBar({ session, onLifecycle }: {
  session: ContextSessionSummary
  onLifecycle?: ContextWorkspaceProps['onLifecycle']
}) {
  const act = session.state === 'active' ? 'save' : session.state === 'saved' ? 'resume' : 'restore'
  const label = act === 'save' ? 'Save' : act === 'resume' ? 'Resume' : 'Restore'
  const icon = act === 'save' ? <Save size={15} /> : act === 'resume' ? <ChevronRight size={15} /> : <RotateCcw size={15} />
  return (
    <div role="group" className={s.lifecycle} aria-label="Session lifecycle">
      <div className={s.sessionState}>
        <Pill tone={session.state === 'active' ? 'good' : session.state === 'saved' ? 'brand' : 'neutral'}>
          {session.state}
        </Pill>
        {session.syncLabel && <span className={s.sync}><Clock3 size={13} aria-hidden />{session.syncLabel}</span>}
      </div>
      <div className={s.lifecycleActions}>
        <Button size="sm" variant="secondary" icon={icon} onClick={() => onLifecycle?.(act)}>{label}</Button>
        {session.state !== 'archived' && (
          <Button size="sm" variant="ghost" icon={<Archive size={15} />} onClick={() => onLifecycle?.('archive')}>Archive</Button>
        )}
      </div>
    </div>
  )
}

function ContextTray({ entries, onAdd, onOpen }: {
  entries: readonly ContextTrayEntry[]
  onAdd?: () => void
  onOpen?: (id: string) => void
}) {
  return (
    <aside className={s.tray} aria-label="Context tray">
      <div className={s.regionHead}>
        <div><span className={s.kicker}>Sources</span><h3>Context tray</h3></div>
        <Button size="sm" variant="primary" icon={<Plus size={15} />} onClick={onAdd}>Add</Button>
      </div>
      <button type="button" className={s.search} onClick={onAdd}><Search size={15} aria-hidden />Search permitted sources</button>
      <div className={s.trayList}>
        {entries.map((entry) => (
          <button type="button" className={s.trayItem} key={entry.id} onClick={() => onOpen?.(entry.id)}>
            <span className={s.trayGlyph} aria-hidden><Box size={15} /></span>
            <span className={s.trayCopy}>
              <span className={s.trayKind}>{entry.kind}</span>
              <strong>{entry.title}</strong>
              {entry.detail && <small>{entry.detail}</small>}
            </span>
            {entry.count != null && <BadgeNumber value={entry.count} />}
          </button>
        ))}
      </div>
    </aside>
  )
}

function BadgeNumber({ value }: { value: number }) {
  return <span role="img" className={s.count} aria-label={`${value} items`}>{value}</span>
}

function WorkbenchBlock({ block, onOpen }: { block: ContextBlock; onOpen?: (id: string) => void }) {
  return (
    <article className={s.block} data-kind={block.kind} data-source-state={block.sourceState} data-tone={block.tone}>
      <div className={s.blockHead}>
        <div>
          <span className={s.blockKind}>{BLOCK_LABEL[block.kind]}</span>
          <h3>{block.title}</h3>
        </div>
        {block.sourceState && <Pill tone={SOURCE_TONE[block.sourceState]}>{SOURCE_LABEL[block.sourceState]}</Pill>}
      </div>
      {block.summary && <p className={s.blockSummary}>{block.summary}</p>}
      {block.content && <div className={s.blockContent}>{block.content}</div>}
      <div className={s.blockFoot}>
        <span>{block.meta ?? 'Session evidence'}</span>
        {onOpen && <button type="button" onClick={() => onOpen(block.id)}>Inspect <ChevronRight size={14} aria-hidden /></button>}
      </div>
    </article>
  )
}

function InvestigationThread({ entries, composerValue, onComposerChange, onSubmitPrompt, composerBusy }: {
  entries: readonly ContextThreadEntry[]
  composerValue?: string
  onComposerChange?: (value: string) => void
  onSubmitPrompt?: () => void
  composerBusy?: boolean
}) {
  return (
    <ol className={s.threadList} aria-label="Investigation thread">
      {entries.map((entry) => (
        <li key={entry.id} className={s.turn} data-kind={entry.kind}>
          <div className={s.turnHead}><strong>{entry.actor}</strong><span>{entry.kind}{entry.time ? ` · ${entry.time}` : ''}</span></div>
          <p>{entry.body}</p>
          {entry.linkedBlockTitle && <span className={s.linked}>Linked to {entry.linkedBlockTitle}</span>}
        </li>
      ))}
      {onSubmitPrompt && (
        <li className={s.composer}>
          <label htmlFor="context-thread-prompt">Ask within this Context</label>
          <textarea id="context-thread-prompt" value={composerValue ?? ''}
            onChange={(event) => onComposerChange?.(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                if (!composerBusy && composerValue?.trim()) onSubmitPrompt()
              }
            }}
            placeholder="Ask, compare, explain or propose a reviewed plan…" rows={3} />
          <Button size="sm" disabled={composerBusy || !composerValue?.trim()}
            loading={composerBusy} onClick={onSubmitPrompt}>Ask Copilot</Button>
        </li>
      )}
    </ol>
  )
}

function ActionPreview({ actions, onRefresh, onReview, onHandoff }: {
  actions: readonly ContextActionEntry[]
  onRefresh?: (id: string) => void
  onReview?: (id: string) => void
  onHandoff?: (id: string) => void
}) {
  return (
    <div role="group" className={s.actionList} aria-label="Action plan">
      {actions.map((action) => (
        <article className={s.action} key={action.id}>
          <div className={s.actionHead}><strong>{action.title}</strong><Pill tone={ACTION_TONE[action.status]}>{action.status}</Pill></div>
          <p>{action.consequence}</p>
          {(action.affected != null || action.refused != null) && (
            <div className={s.population}>
              {action.affected != null && <span><Check size={14} aria-hidden />{action.affected} affected</span>}
              {action.refused != null && action.refused > 0 && <span>{action.refused} refused</span>}
            </div>
          )}
          <div className={s.actionButtons}>
            {action.reviewable && <Button size="sm" variant="secondary" onClick={() => onReview?.(action.id)}>Review latest impact</Button>}
            {action.handoffable && <Button size="sm" variant="primary" onClick={() => onHandoff?.(action.id)}>Hand off task</Button>}
            {action.refreshable && <Button size="sm" variant="secondary" onClick={() => onRefresh?.(action.id)}>Refresh real outcome</Button>}
          </div>
        </article>
      ))}
    </div>
  )
}

function RelationRouteMap({ relations, onOpen }: {
  relations: readonly ContextRelationEntry[]
  onOpen?: (id: string) => void
}) {
  if (relations.length === 0) return (
    <div className={s.relationEmpty}><GitBranch size={20} aria-hidden /><strong>No explicit routes yet</strong><span>Add records, then describe how the evidence connects.</span></div>
  )
  return (
    <ol className={s.routeMap} aria-label="Context relationship routes">
      {relations.map((relation) => (
        <li key={relation.id} data-kind={relation.kind}>
          <button type="button" onClick={() => onOpen?.(relation.fromId)}><span className={s.station} aria-hidden /><strong>{relation.fromTitle}</strong></button>
          <div className={s.routeLine}><span>{relation.kind.replaceAll('_', ' ')}</span>{relation.confidence != null && <small>{Math.round(relation.confidence * 100)}% confidence</small>}</div>
          <button type="button" onClick={() => onOpen?.(relation.toId)}><span className={s.station} aria-hidden /><strong>{relation.toTitle}</strong></button>
          {relation.rationale && <p>{relation.rationale}</p>}
        </li>
      ))}
    </ol>
  )
}

export function ContextWorkspace({
  session, tray, blocks, thread, actions, relations = [], mode, onModeChange, onAddContext,
  onOpenTrayEntry, onOpenBlock, onLifecycle, composerValue, onComposerChange,
  onSubmitPrompt, composerBusy, onRefreshAction, testId = 'context-workspace',
  onReviewAction, onHandoffAction,
}: ContextWorkspaceProps) {
  return (
    <section className={s.workspace} data-mode={mode} data-testid={testId} aria-label="Marble Context workspace">
      <header className={s.header}>
        <div className={s.heading}>
          <span className={s.eyebrow}>{session.eyebrow ?? 'Marble Context'}</span>
          <h2>{session.title}</h2>
          <p>{session.objective}</p>
        </div>
        <SessionLifecycleBar session={session} onLifecycle={onLifecycle} />
      </header>

      <div className={s.mobileModes}>
        <SegmentedControl label="Context workspace view" size="sm" value={mode} onChange={onModeChange}
          options={[{ value: 'canvas', label: 'Canvas' }, { value: 'relations', label: 'Map' }, { value: 'thread', label: 'Thread' }, { value: 'actions', label: 'Actions' }]} />
      </div>

      <div className={s.body}>
        <ContextTray entries={tray} onAdd={onAddContext} onOpen={onOpenTrayEntry} />
        <main className={s.canvas} aria-label="Context canvas">
          <div className={s.canvasHead}>
            <div><span className={s.kicker}>Investigation</span><h3>Evidence and reasoning</h3></div>
            <div className={s.canvasTools}><span>{blocks.length} blocks</span><SegmentedControl label="Canvas presentation" size="sm" value={mode === 'relations' ? 'relations' : 'canvas'} onChange={onModeChange} options={[{ value: 'canvas', label: 'Cards' }, { value: 'relations', label: 'Route map' }]} /></div>
          </div>
          {/* The drawing surface: the dot grid lies under the blocks, never
              under the head's words (see `.stage` in the stylesheet). */}
          <div className={s.stage}>
            {mode === 'relations'
              ? <RelationRouteMap relations={relations} onOpen={onOpenBlock} />
              : <div className={s.blocks}>{blocks.map((block) => <WorkbenchBlock key={block.id} block={block} onOpen={onOpenBlock} />)}</div>}
          </div>
        </main>
        <aside className={s.inspector} aria-label={mode === 'actions' ? 'Context actions' : 'Context thread'}>
          <div className={s.inspectorTabs} role="tablist" aria-label="Context inspector">
            {(['thread', 'actions'] as const).map((value) => (
              <button type="button" role="tab" key={value} aria-selected={mode === value}
                onClick={() => onModeChange(value)}>{value === 'thread' ? 'Thread' : 'Actions'}</button>
            ))}
          </div>
          <div className={s.inspectorBody}>
            {mode === 'actions' ? <ActionPreview actions={actions} onRefresh={onRefreshAction} onReview={onReviewAction} onHandoff={onHandoffAction} /> : (
              <InvestigationThread entries={thread} composerValue={composerValue}
                onComposerChange={onComposerChange} onSubmitPrompt={onSubmitPrompt}
                composerBusy={composerBusy} />
            )}
          </div>
        </aside>
      </div>
    </section>
  )
}
